import { useCallback, useEffect, useState } from 'react'
import { getSubmittedWebinarQuestions } from '@domain/features/webinars/webinars-feature'
import type { WebinarQuestion } from '@domain/models/webinar'

interface UseSubmittedWebinarQuestionsOptions {
  enabled?: boolean
}

export function useSubmittedWebinarQuestions(
  webinarId: string | undefined,
  options?: UseSubmittedWebinarQuestionsOptions,
) {
  const [questions, setQuestions] = useState<WebinarQuestion[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadTick, setReloadTick] = useState(0)

  const reload = useCallback(() => {
    setReloadTick((current) => current + 1)
  }, [])

  useEffect(() => {
    if (!webinarId || options?.enabled === false) {
      setQuestions([])
      setError(null)
      setLoading(false)
      return
    }
    const resolvedWebinarId = webinarId

    let cancelled = false

    async function load() {
      try {
        setLoading(true)
        setError(null)
        const result = await getSubmittedWebinarQuestions(resolvedWebinarId)
        if (!cancelled) {
          setQuestions(result)
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load submitted webinar questions.',
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [options?.enabled, reloadTick, webinarId])

  return { questions, loading, error, reload }
}
